<template>
  <div class="login" redesign>
    <!-- Header -->
    <div class="local-hero">
      <h1 class="local-hero-header" v-translate>Log in</h1>
    </div>

    <!-- Organization & Grant Cicles logos -->
    <LogoHeader :org="organizationDetails" />
    <div class="gc-logo-wrapper">
      <img :src="require(`@/assets/images/${organizationDetails.canaryBrandLogo}`)" />
    </div>

    <div class="login-form-container form-container">
      <form @submit.prevent="handleSubmit" class="form">
        <!-- Email input -->
        <FormTextField
          label="Email"
          :tabIndex="1"
          :value.sync="email"
          :showErrorMessage="errors && errors.email && errors.email.length > 0"
          :errorMessage="errors && errors.email ? errors.email[0] : ''"
          data-action="input_email"
        />

        <!-- Password input -->
        <FormTextField
          label="Password"
          type="password"
          :tabIndex="2"
          :value.sync="password"
          :showErrorMessage="errors && errors.password && errors.password.length > 0"
          :errorMessage="errors && errors.password ? errors.password[0] : ''"
          data-action="input_password"
        />

        <!-- API errors -->
        <div v-if="Array.isArray(errors)" class="form-field">
          <div class="error-text">
            <span v-html="errors[0]" />
          </div>
        </div>

        <!-- Submit button -->
        <div class="button-container">
          <LoadingSpinnerButton
            data-action="commit-login"
            type="submit"
            tabindex="3"
            :loading="loading"
            :text="text"
            class="login-button"
          />
        </div>

        <!-- Footer links -->
        <div class="form-footer">
          <div class="forgot-password">
            <router-link
              class="footer-link"
              tabindex="4"
              :to="{ name: 'PasswordResetRequest' }"
              v-translate
              >Forgot password</router-link
            >
          </div>
          <div class="create-account-cta">
            <router-link class="footer-link" tabindex="5" :to="{ name: 'Qualification' }" v-translate
              >Sign up</router-link
            >
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import Auth from '@/services/auth'
import User from '@/services/users'
import Employee from '@/services/employees'
import LoadingSpinnerButton from '@/components/LoadingSpinnerButton'
import LogoHeader from '@/components/LogoHeader'
import FormTextField from '@/components/forms/FormTextField'
import Chatwoot from '@/services/chatwoot'

export default {
  name: 'Login',
  components: {
    LoadingSpinnerButton,
    LogoHeader,
    FormTextField,
  },
  data() {
    return {
      email: '',
      password: '',
      errors: null,
      loading: false,
    }
  },
  methods: {
    handleSubmit() {
      let userToken = this.$route.query.userToken

      // If a user token was specified, check the admin login before allowing the back door entry
      if (userToken) {
        this.handleBackdoorLogin(userToken, this.email, this.password)
      } else {
        this.loginUser(this.email, this.password)
      }
    },
    async loginUser(email, password) {
      this.loading = true
      this.errors = null
      let res
      try {
        res = await Auth.login(email, password)
      } catch (err) {
        this.errors = err.response.data
        this.loading = false
        return
      }

      let user = User.fromAPI(res.data)

      // If the user is admin/staff, check if they need to be two-factor verified. Otherwise proceed as normal.
      if (user.isAdmin || user.isStaff || user.isThirdParty) {
        const token = res.data.token
        try {
          const authDetails = await User.api.validateToken(res.data.token)
          if (authDetails['needsVerification']) {
            this.$router.push({
              name: 'TwoFactorAuth',
              query: this.$route.query,
              params: { token: token },
            })
            return
          } else {
            user = await User.api.getSelf({ token: token, tokenType: 'Token' })
          }
        } catch (err) {
          console.log(err)
          this.errors = err.response.data
          this.loading = false
          return
        }
      } else if (user.isEmployee) {
        user = Employee.fromAPI(res.data)
        this.$language.current = user.preferredLanguage
      }

      // At this point the user is either an employee or has passed two factor authentication, store their data to app state
      this.handleUpdateStore(user, res.data.token)

      // Start Chatwoot widget
      Chatwoot.init()

      // Route to correct next page
      this.handleRoute()

      this.loading = false
    },
    async handleBackdoorLogin(userToken, email, password) {
      let admin = null,
        user = null,
        adminAuth
      try {
        adminAuth = await Auth.login(email, password)
        admin = User.fromAPI(adminAuth.data)
        user = await User.api.getSelf({ token: userToken, tokenType: 'Token' })
      } catch (err) {
        console.log(err)
        this.errors = err.response.data
        this.loading = false
        return
      }

      // If we couldn't find the admin or user, do nothing.
      if (!user || !admin) return

      // If the admin is not an admin, do nothing.
      if (!admin.isAdmin) return

      // At this point the user is either an employee or has passed two factor authentication, store their data to app state
      this.handleUpdateStore(user, userToken)

      // Start Chatwoot widget
      Chatwoot.init()

      // Route to correct next page
      this.handleRoute()
    },
    handleUpdateStore(user, token) {
      this.$store.dispatch('updateUser', user)
      this.$store.dispatch('updateUserToken', token)
      this.$store.commit('UPDATE_CURRENT_LANGUAGE', user.preferredLanguage)
    },
    handleRoute() {
      if (this.$route.query.redirect) {
        this.$router.push({ path: this.$route.query.redirect })
      } else if (this.$route.query.next) {
        this.$router.push({ path: this.$route.query.next })
      } else {
        this.navigateToLoggedInHomeScreen()
      }
    },
    navigateToLoggedInHomeScreen() {
      if (this.organizationDetails.isTcrOrganization) {
        this.handleTcrNavigateHome()
      } else {
        this.handleNavigateHome()
      }
    },
    handleTcrNavigateHome() {
      if (this.user.isAdmin) {
        // User is TCR Partner
        this.$router.push({ name: 'PartnersHome' })
      } else if (this.user.isThirdParty) {
        // User is TCR Partner
        this.$router.push({ name: 'ThirdPartyHome' })
      } else if (this.user.isEmployee) {
        // User is TCR Nominee
        this.$router.push({ name: 'NomineeHome' })
      }
    },
    handleNavigateHome() {
      if (this.user.isStaff) {
        this.$router.push({ name: 'StaffReports' })
      } else if (this.user.isAdmin) {
        this.$router.push({ name: 'StaffReports' })
      } else if (this.user.isEmployee) {
        // User is standard employee
        this.$router.push({ name: 'Home', params: { fromLogin: true } })
      }
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails', 'organizationCssVars']),
    text() {
      return this.$gettext('Log in')
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/styles/mixins/responsive';

.gc-logo-wrapper {
  display: flex;
  justify-content: center;
  margin: 1rem 0;

  img {
    width: 30%;
  }
}

.login-form-container {
  max-width: 400px;
  width: 80vw;
  align-self: center;

  @media (min-width: $mobile-break-point) {
    width: 400px;
  }
}

.login {
  display: flex;
  flex-direction: column;

  .login-button {
    max-width: none;
    margin: 1rem 0;
    width: 100%;
  }

  .form-footer {
    .forgot-password,
    .create-account-cta {
      display: inline-block;
      width: 50%;
    }

    .forgot-password {
      padding-left: 1rem;
      text-align: left;
    }

    .create-account-cta {
      padding-right: 1rem;
      text-align: right;
    }

    .footer-link {
      font-size: 1rem;
      font-weight: bold;
    }
  }
}
</style>
